// Ctrl+K command palette: universal search (tasks, locations, docs, chats, people)
// plus quick commands. Rendered by AppShell while useUi.commandOpen is true.
import '@/styles/command.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { ArrowDown, ArrowUp, ChevronRight, CornerDownLeft, Search } from '@/components/ui/icons';
import { useDb, useMe } from '@/lib/store';
import { useUi } from '@/lib/ui';
import { useOpenTask, useWorkspaceId } from '@/lib/nav';
import { Highlight, tokenize } from './search';
import { buildGroups, FILTERS, type FilterKey, type PaletteItem } from './results';

const close = () => useUi.setState({ commandOpen: false });

export function CommandPalette() {
  const ws = useWorkspaceId();
  const me = useMe();
  const db = useDb((d) => d);
  const recents = useUi((s) => s.recents);
  const theme = useUi((s) => s.theme);
  const navigate = useNavigate();
  const openTask = useOpenTask();

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // keep latest router helpers without invalidating the memoized results
  const nav = useRef({ navigate, openTask });
  nav.current = { navigate, openTask };

  const groups = useMemo(
    () =>
      buildGroups(
        {
          db,
          ws,
          me,
          theme,
          recents,
          go: (p) => nav.current.navigate(p),
          openTask: (id) => nav.current.openTask(id),
          setFilter,
        },
        query,
        filter,
      ),
    [db, ws, me, theme, recents, query, filter],
  );
  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const words = useMemo(() => tokenize(query), [query]);

  useEffect(() => setActive(0), [query, filter]);
  useEffect(() => {
    if (active >= flat.length) setActive(Math.max(0, flat.length - 1));
  }, [flat.length, active]);

  useEffect(() => {
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  // Escape closes the palette before any modal underneath sees it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        close();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active, flat]);

  const activate = useCallback((item: PaletteItem | undefined) => {
    if (!item) return;
    if (item.keepOpen) {
      item.run();
      inputRef.current?.focus();
      return;
    }
    close();
    item.run();
  }, []);

  const cycleFilter = (dir: 1 | -1) => {
    const i = FILTERS.findIndex((f) => f.key === filter);
    setFilter(FILTERS[(i + dir + FILTERS.length) % FILTERS.length].key);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (flat.length) setActive((a) => (a + 1) % flat.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (flat.length) setActive((a) => (a - 1 + flat.length) % flat.length);
    } else if (e.key === 'Enter') {
      // Enter and Ctrl/Cmd+Enter both activate; for tasks that opens the task modal.
      e.preventDefault();
      activate(flat[active]);
    } else if (e.key === 'Tab') {
      e.preventDefault();
      cycleFilter(e.shiftKey ? -1 : 1);
    } else if (e.key === 'Backspace' && query === '' && filter !== 'all') {
      e.preventDefault();
      setFilter('all');
    }
  };

  let idx = -1;
  const empty = flat.length === 0;

  return createPortal(
    <div className="cmdk-overlay" onMouseDown={(e) => e.target === e.currentTarget && close()}>
      <div className="cmdk" role="dialog" aria-modal="true" aria-label="Command palette" onMouseDown={(e) => e.target !== inputRef.current && e.preventDefault()}>
        <div className="cmdk-input-row">
          <Search size={18} className="cmdk-search-icon" />
          <input
            ref={inputRef}
            className="cmdk-input"
            value={query}
            autoFocus
            placeholder="Search, run a command, or ask a question…"
            spellCheck={false}
            autoComplete="off"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            role="combobox"
            aria-expanded="true"
            aria-controls="cmdk-list"
            aria-activedescendant={flat[active] ? `cmdk-item-${active}` : undefined}
          />
          <span className="kbd cmdk-esc">Esc</span>
        </div>

        <div className="cmdk-filters" role="tablist">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              role="tab"
              aria-selected={filter === f.key}
              className={clsx('cmdk-chip', { active: filter === f.key })}
              onClick={() => {
                setFilter(f.key);
                inputRef.current?.focus();
              }}
            >
              {f.label}
            </button>
          ))}
          <span className="spacer" />
          <span className="cmdk-filters-hint">
            <span className="kbd">Tab</span> to switch
          </span>
        </div>

        <div className="cmdk-list" id="cmdk-list" ref={listRef} role="listbox">
          {empty && (
            <div className="cmdk-empty">
              <Search size={22} />
              <div className="cmdk-empty-title">{query.trim() ? `No results for “${query.trim()}”` : 'Nothing here yet'}</div>
              <div className="faint">
                {filter !== 'all' ? (
                  <button className="cmdk-link" onClick={() => setFilter('all')}>
                    Search everything instead
                  </button>
                ) : (
                  'Try different keywords or a task ID.'
                )}
              </div>
            </div>
          )}
          {groups.map((g) => (
            <div key={g.key} className="cmdk-group" role="group" aria-label={g.label}>
              <div className="cmdk-group-label">
                {g.label}
                {query.trim() && g.key !== 'recent' && g.key !== 'commands' && <span className="cmdk-group-count">{g.total}</span>}
              </div>
              {g.items.map((item) => {
                idx += 1;
                const i = idx;
                const isActive = i === active;
                if (item.kind === 'more') {
                  return (
                    <div
                      key={item.key}
                      id={`cmdk-item-${i}`}
                      data-idx={i}
                      role="option"
                      aria-selected={isActive}
                      className={clsx('cmdk-item cmdk-more', { active: isActive })}
                      onMouseMove={() => i !== active && setActive(i)}
                      onClick={() => activate(item)}
                    >
                      <span className="cmdk-item-icon" />
                      <span className="cmdk-more-label">{item.title}</span>
                      <ChevronRight size={14} />
                    </div>
                  );
                }
                return (
                  <div
                    key={item.key}
                    id={`cmdk-item-${i}`}
                    data-idx={i}
                    role="option"
                    aria-selected={isActive}
                    className={clsx('cmdk-item', `kind-${item.kind}`, { active: isActive })}
                    onMouseMove={() => i !== active && setActive(i)}
                    onClick={() => activate(item)}
                  >
                    <span className="cmdk-item-icon">{item.icon}</span>
                    <span className="cmdk-item-text">
                      <span className="cmdk-item-title">{item.plain ? item.title : <Highlight text={item.title} words={words} />}</span>
                      {item.meta && <span className="cmdk-item-meta">{item.meta}</span>}
                    </span>
                    {item.right && <span className="cmdk-item-right">{item.right}</span>}
                    <span className="cmdk-item-enter">
                      <CornerDownLeft size={13} />
                    </span>
                  </div>
                );
              })}
              {g.hidden > 0 && <div className="cmdk-hidden-note">{g.hidden} more — keep typing to narrow down</div>}
            </div>
          ))}
        </div>

        <div className="cmdk-footer">
          <span className="cmdk-hint">
            <span className="kbd">
              <ArrowUp size={11} />
            </span>
            <span className="kbd">
              <ArrowDown size={11} />
            </span>
            Navigate
          </span>
          <span className="cmdk-hint">
            <span className="kbd">
              <CornerDownLeft size={11} />
            </span>
            Open
          </span>
          <span className="cmdk-hint">
            <span className="kbd">Ctrl</span>
            <span className="kbd">
              <CornerDownLeft size={11} />
            </span>
            Open task
          </span>
          <span className="spacer" />
          <span className="cmdk-hint">
            <span className="kbd">Esc</span>
            Close
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
